import React from 'react';
import { Col, Row } from 'react-bootstrap';
import KeyFactsBar from '@/components/molecules/de-uni/key-facts-bar/key-facts-bar';
import {
  AdvertBanner,
  ApplicationBranding,
  Hero,
  MediaGallery,
  getAdvertBannerContent,
  checkAdvertBannerContentExists,
  IAdvertBanner,
} from '@into-discovery/browsewebshared';
import Branding from '@/components/molecules/de-uni/branding/branding';
import UniversityRanking from '@/components/molecules/de-uni/ranking/ranking';
import BrandLandingIntro from '@/components/molecules/de-uni/brand-intro/brand-intro';
import Campuses from '@/components/organisms/de-uni/campuses/campuses';
import CostsAndEntryReqs from '@/components/organisms/de-uni/costs-and-entry-reqs/costs-and-entry-reqs';
import Accommodation from '@/components/molecules/de-uni/accommodation/accommodation';
import TopRankedSubjects from '@/components/molecules/de-uni/top-ranked-subjects/top-ranked-subjects';
import HowToApply from '@/components/molecules/de-uni/how-to-apply/how-to-apply';
import TextBlock from '@/components/atoms/text-block/text-block';
import Downloads from '@/components/molecules/de-uni/downloads/downloads';
import IBrandLandingPageViewModel from '@/models/viewModels/IBrandLandingPageViewModel';
import RelatedInstitutions from '@/components/molecules/de-uni/related-institutions/related-institutions';
import StudyPrograms from '@/components/organisms/de-uni/study-programs/study-programs';
import colorStyles from '@/styles/utils/variables.module.scss';
import styles from './page-contnet.module.scss';

type Props = {
  content: IBrandLandingPageViewModel;
  mapsApiKey: string;
  searchBrandId: string;
};

export default function PageContent({
  content: {
    accommodation,
    brand,
    campuses,
    costsAndEntryRequirements,
    downloads,
    hero,
    highlightRanking,
    howToApply,
    rankings,
    advertBanner,
    keyFacts,
    servicesAndFacilities,
    studyPrograms,
    studyProgramsIntro,
    mediaGallery,
    relatedInstitutions,
    topRankedSubjects,
    studentSuccess,
    universityIntro,
    totalStudyPrograms,
    careersIntro,
  },
  mapsApiKey,
  searchBrandId,
}: Props) {
  const appBranding: ApplicationBranding = {
    primaryColor: colorStyles.primary,
    primaryHoverColor: colorStyles.primaryHover,
    primaryActiveColor: colorStyles.primaryActive,
  };

  let advertBannerContent: IAdvertBanner;
  let checkAdvertExists = false;
  if (advertBanner) {
    advertBannerContent = getAdvertBannerContent(advertBanner);

    // check if advert banner data exist
    checkAdvertExists = checkAdvertBannerContentExists(advertBanner);
  }

  return (
    <>
      <div className={`overview ${styles[`component-container`]}`}>
        {hero ? (
          <div className="overflow-x-hidden">
            <Hero src={hero.secureUrl} altText={hero.alt} />
          </div>
        ) : null}
        {brand ? <Branding brand={brand} highlightRanking={highlightRanking} /> : null}
        {keyFacts ? (
          <KeyFactsBar keyFacts={keyFacts} uniBackgroundColor={brand.primaryColour} />
        ) : null}
        {relatedInstitutions?.length ? (
          <RelatedInstitutions relatedInstitutions={relatedInstitutions} />
        ) : null}
        {universityIntro ? (
          <BrandLandingIntro
            brandName={brand.name}
            brandIntro={universityIntro}
            metrics={studentSuccess}
          />
        ) : null}
      </div>
      {rankings?.length ? (
        <UniversityRanking rankings={rankings} brandColour={brand.primaryColour} />
      ) : null}
      {advertBanner && checkAdvertExists ? (
        <AdvertBanner
          advertBanner={advertBannerContent}
          appBranding={appBranding}
          application={process.env.NEXT_PUBLIC_APPLICATION_NAME}
        />
      ) : null}
      {campuses?.length ? (
        <Campuses
          mapsApiKey={mapsApiKey}
          universityName={brand.name}
          universityPrimaryColor={brand.primaryColour}
          campuses={campuses}
        />
      ) : null}
      {downloads?.length ? <Downloads files={downloads} /> : null}
      {costsAndEntryRequirements?.length ? (
        <CostsAndEntryReqs
          costsAndEntryRequirements={costsAndEntryRequirements}
          universityPrimaryColor={brand.primaryColour}
        />
      ) : null}

      {topRankedSubjects?.length ? (
        <TopRankedSubjects
          topRankedSubjects={topRankedSubjects}
          universityPrimaryColor={brand.primaryColour}
        />
      ) : null}
      {studyPrograms?.length ? (
        <StudyPrograms
          studyPrograms={studyPrograms}
          searchBrandId={searchBrandId}
          universityPrimaryColor={brand.primaryColour}
          totalStudyPrograms={totalStudyPrograms}
          studyProgramsIntro={studyProgramsIntro}
        />
      ) : null}
      {careersIntro ? (
        <section id="careers">
          <Row>
            <Col lg="8">
              <TextBlock title="Careers" text={careersIntro} titleSize={2} />
            </Col>
          </Row>
        </section>
      ) : null}
      {accommodation ? <Accommodation accommodation={accommodation} /> : null}
      {servicesAndFacilities ? (
        <section id="services-and-facilities" className="section-separator">
          <Row>
            <Col lg="8">
              <TextBlock
                title="Services and facilities"
                text={servicesAndFacilities}
                titleSize={2}
              />
            </Col>
          </Row>
        </section>
      ) : null}
      {mediaGallery?.length > 0 ? (
        <section className="mt-5" id="video-and-gallery" aria-label="Video and gallery">
          <h2 className="mb-4">Video & Gallery</h2>
          <MediaGallery galleryItems={mediaGallery} />
        </section>
      ) : null}
      {howToApply?.length ? <HowToApply howToApply={howToApply} /> : null}
    </>
  );
}
